// src/components/fix-actions/redesign/asset-generation-tray.tsx
//
// Redesigned asset generation slide-over. Same prop contract as the legacy
// AssetGenerationTray; the page drives mode transitions and all real service
// calls (onGenerate / onApprove / onRegenerate). Model names come from the
// real catalog; the review panel renders the real fullContent.

import { useEffect, useState } from "react";
import type { StrategyArtifact, ArtifactType } from "../../../services/fix-actions/optimization-studio-service";
import { getModelDisplayName } from "../../../constants/models";
import { FullContentRenderer } from "../full-content-renderer";
import { typeColor, ASSET_TYPE_INFO, Ic, SPARKLE } from "./shared";
import { ClaimCheckBadge, ClaimCheckDetails } from "../artifact-claim-check";
import { ExplainPopover, type ExplainAnchor } from "./explain-popover";
import "./action-items-redesign.css";

export type TrayMode = "setup" | "generating" | "review";

export interface GenerationConfig {
  brand: string;
  category: string;
  competitors: string[];
  deploymentTargets: { ownedSite: boolean; thirdPartyBlogs: boolean; forums: boolean };
  tone: string;
  riskSensitivity: string;
}

export interface RedesignGenerationTrayProps {
  mode: TrayMode;
  artifact: StrategyArtifact | null;
  supportingActionTitle: string;
  supportingActionTheme: string;
  artifactType: ArtifactType;
  objective: string;
  outputTypeDescription: string;
  isAssistantCollapsed: boolean;
  onClose: () => void;
  onGenerate: (config: GenerationConfig) => void;
  onApprove: () => void;
  onRegenerate: () => void;
  initialConfig?: Partial<GenerationConfig>;
  ownedSiteLabel?: string;
  thirdPartyBlogsLabel?: string;
  forumsLabel?: string;
  modelsUsed?: string[];
}

function extractText(fullContent: Record<string, any> | undefined, fallback: string): string {
  if (!fullContent) return fallback;
  const c: any = fullContent;
  for (const k of ["text", "body", "copy", "content", "markdown"]) {
    if (typeof c[k] === "string" && c[k].trim()) return c[k];
  }
  try {
    return JSON.stringify(fullContent, null, 2);
  } catch {
    return fallback;
  }
}

export function RedesignAssetGenerationTray(props: RedesignGenerationTrayProps) {
  const {
    mode, artifact, supportingActionTitle, supportingActionTheme, artifactType,
    isAssistantCollapsed, onClose, onGenerate, onApprove, onRegenerate, initialConfig,
    modelsUsed = [],
  } = props;

  const [config, setConfig] = useState<GenerationConfig>({
    brand: initialConfig?.brand ?? "",
    category: initialConfig?.category ?? "",
    competitors: initialConfig?.competitors ?? [],
    deploymentTargets: {
      ownedSite: initialConfig?.deploymentTargets?.ownedSite ?? true,
      thirdPartyBlogs: initialConfig?.deploymentTargets?.thirdPartyBlogs ?? true,
      forums: initialConfig?.deploymentTargets?.forums ?? false,
    },
    tone: initialConfig?.tone ?? "Neutral",
    riskSensitivity: initialConfig?.riskSensitivity ?? "Standard",
  });
  const [copied, setCopied] = useState(false);
  const [explain, setExplain] = useState<ExplainAnchor | null>(null);

  useEffect(() => {
    setConfig((c) => ({
      ...c,
      brand: initialConfig?.brand ?? c.brand,
      category: initialConfig?.category ?? c.category,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig?.brand, initialConfig?.category]);

  const tc = typeColor(String(artifactType));
  const width = isAssistantCollapsed ? 680 : 680;

  const explainType = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setExplain({ type: String(artifactType), x: r.left, y: r.bottom + 8 });
  };
  const typePill = (
    <button onClick={explainType} title="What is this?" style={{ fontSize: 12, fontWeight: 600, color: tc, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: "2px", textDecorationStyle: "dotted" }}>
      {String(artifactType)}
    </button>
  );
  const supportLine = (
    <div style={{ fontSize: 13, color: "var(--muted-foreground)", marginTop: 5 }}>
      Supporting: <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>{supportingActionTitle || "-"}</strong>
      {supportingActionTheme ? `  ·  ${supportingActionTheme}` : ""}
    </div>
  );

  const ghostBtn = (label: string, iconPaths: string[], onClick?: () => void) => (
    <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, padding: "7px 12px", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", border: "1px solid var(--border)", background: "var(--background)", color: "var(--foreground)" }}>
      <Ic paths={iconPaths} size={14} sw={1.8} /> {label}
    </button>
  );

  const info = ASSET_TYPE_INFO[String(artifactType)];

  let header: React.ReactNode = null;
  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  if (mode === "setup") {
    header = (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", margin: 0 }}>Generate {String(artifactType)}</h3>
          {typePill}
        </div>
        {supportLine}
      </div>
    );
    const surface = (name: string, desc: string, on: boolean, toggle: () => void) => (
      <label onClick={toggle} style={{ display: "flex", gap: 11, padding: "13px 14px", border: `1px solid ${on ? "var(--alpha-teal-30, var(--alpha-teal))" : "var(--border)"}`, background: on ? "var(--alpha-teal-5)" : "var(--background)", borderRadius: 10, cursor: "pointer" }}>
        <span style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center", background: on ? "var(--alpha-teal)" : "var(--background)", border: `1px solid ${on ? "var(--alpha-teal)" : "var(--silver-mist)"}`, color: "var(--accent-foreground)" }}>
          {on && <Ic paths={["m5 12 5 5L20 7"]} size={11} sw={3} />}
        </span>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--foreground)" }}>{name}</div>
          <div style={{ fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5, marginTop: 2 }}>{desc}</div>
        </div>
      </label>
    );
    body = (
      <div>
        <div style={{ background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span className="ai-orb" style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
              <Ic paths={SPARKLE} size={15} sw={1.7} />
            </span>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--foreground)" }}>Signal Analyst</div>
              <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>What this asset produces</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            <span style={{ width: 3, borderRadius: 2, background: "linear-gradient(135deg,var(--spectral-violet),var(--signal-blue))", flexShrink: 0 }} />
            <p style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, letterSpacing: "-.01em", color: "var(--foreground)", margin: 0 }}>
              {info?.blurb || `A structured ${String(artifactType).toLowerCase()} that consolidates the evidence behind "${supportingActionTitle || "this action"}" into one extractable, citable source for AI models.`}
            </p>
          </div>
        </div>

        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--foreground)", marginBottom: 4 }}>Configure</div>
        <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: "0 0 18px" }}>Most values are auto-detected. Adjust if needed, then generate.</p>
        <div style={{ display: "flex", gap: 20, marginBottom: 22, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 180px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--foreground)", marginBottom: 10 }}>Brand</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--muted)" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>{config.brand || "-"}</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: "var(--muted-foreground)", background: "var(--background)", border: "1px solid var(--border)", borderRadius: 5, padding: "1px 6px", marginLeft: "auto" }}>Auto</span>
            </div>
          </div>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--foreground)", marginBottom: 10 }}>Category</div>
            <input
              value={config.category}
              onChange={(e) => setConfig((c) => ({ ...c, category: e.target.value }))}
              style={{ width: "100%", padding: "9px 11px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--background)", fontSize: 13, fontFamily: "var(--font-sans)", color: "var(--foreground)", outline: "none" }}
            />
          </div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--foreground)", marginBottom: 10 }}>Deployment surfaces</div>
        <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: "0 0 12px" }}>Where this asset publishes. Each surface contributes to model learning differently.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {surface(props.ownedSiteLabel || "Owned site", "Direct model citation source. Highest authority signal, fastest indexing.", config.deploymentTargets.ownedSite, () => setConfig((c) => ({ ...c, deploymentTargets: { ...c.deploymentTargets, ownedSite: !c.deploymentTargets.ownedSite } })))}
          {surface(props.thirdPartyBlogsLabel || "Third-party blogs", "Cross-domain validation. Models weight independent sources as stronger consensus.", config.deploymentTargets.thirdPartyBlogs, () => setConfig((c) => ({ ...c, deploymentTargets: { ...c.deploymentTargets, thirdPartyBlogs: !c.deploymentTargets.thirdPartyBlogs } })))}
          {surface(props.forumsLabel || "Forums & communities", "Conversational context. Influences models trained on Q&A patterns.", config.deploymentTargets.forums, () => setConfig((c) => ({ ...c, deploymentTargets: { ...c.deploymentTargets, forums: !c.deploymentTargets.forums } })))}
        </div>
      </div>
    );
    footer = (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
        {ghostBtn("Cancel", ["M18 6 6 18M6 6l12 12"], onClose)}
        <button onClick={() => onGenerate(config)} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 600, padding: "9px 17px", borderRadius: 9, border: "none", background: "var(--alpha-teal)", color: "var(--accent-foreground)", cursor: "pointer" }}>
          Generate Asset <Ic paths={["M5 12h14M13 6l6 6-6 6"]} size={15} sw={2} />
        </button>
      </div>
    );
  } else if (mode === "generating") {
    const models = (modelsUsed || []).slice(0, 4).map((m) => getModelDisplayName(m));
    header = (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="ai-spin" style={{ width: 18, height: 18, border: "2px solid var(--alpha-teal-20)", borderTopColor: "var(--alpha-teal)", borderRadius: 9999, flexShrink: 0 }} />
          <h3 style={{ fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", margin: 0 }}>Generating {String(artifactType)}</h3>
          {typePill}
        </div>
        {supportLine}
      </div>
    );
    body = (
      <div>
        <h4 style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, margin: "0 0 8px" }}>Building a reusable, citation-ready asset for your category.</h4>
        <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", lineHeight: 1.6, margin: "0 0 22px" }}>
          This asset will influence how AI models evaluate options and make recommendations to users in your category.
        </p>
        <div style={{ borderLeft: "3px solid var(--alpha-teal)", paddingLeft: 18, marginBottom: 20, display: "flex", alignItems: "center", gap: 11 }}>
          <span className="ai-spin" style={{ width: 18, height: 18, border: "2px solid var(--alpha-teal-20)", borderTopColor: "var(--alpha-teal)", borderRadius: 9999, flexShrink: 0 }} />
          <span style={{ fontSize: 13.5, color: "var(--foreground)", fontWeight: 500 }}>Analyzing, drafting, and validating across models…</span>
        </div>
        {models.length > 0 && (
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--muted-foreground)" }}>Models</span>
            {models.map((m, i) => (
              <span key={i} style={{ fontSize: 11, fontWeight: 600, color: "var(--foreground)", border: "1px solid var(--border)", borderRadius: 6, padding: "3px 9px" }}>{m}</span>
            ))}
          </div>
        )}
        <div style={{ marginTop: 20, background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px" }}>
          <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>
            You can minimize this panel to keep working. The asset appears in your Optimization Studio when it's ready.
          </p>
        </div>
      </div>
    );
    footer = (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>{ghostBtn("Minimize", ["M5 12h14"], onClose)}</div>
    );
  } else {
    // review
    const text = extractText(artifact?.fullContent, artifact?.name || "");
    const surfaces = [
      config.deploymentTargets.ownedSite && (props.ownedSiteLabel || "Owned site"),
      config.deploymentTargets.thirdPartyBlogs && (props.thirdPartyBlogsLabel || "Third-party blogs"),
      config.deploymentTargets.forums && (props.forumsLabel || "Forums & communities"),
    ].filter(Boolean) as string[];
    const reviewTargets = (artifact?.deploymentTargets && artifact.deploymentTargets.length ? artifact.deploymentTargets : surfaces);
    header = (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", margin: 0 }}>{artifact?.name || String(artifactType)}</h3>
          {typePill}
        </div>
        {supportLine}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 9 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 9999, background: "var(--muted)", color: "var(--muted-foreground)", border: "1px solid var(--border)" }}>Draft</span>
          <ClaimCheckBadge check={artifact?.claimCheck} />
        </div>
      </div>
    );
    body = (
      <div>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "rgba(18,183,106,.06)", border: "1px solid var(--success-border)", borderRadius: 14, padding: "18px 20px", marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)", marginBottom: 10 }}>Ready for review</div>
            <div style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.7 }}>
              <div><strong style={{ color: "var(--foreground)", fontWeight: 600 }}>Type:</strong> {String(artifactType)}</div>
              <div><strong style={{ color: "var(--foreground)", fontWeight: 600 }}>Publishing to:</strong> {reviewTargets.length ? reviewTargets.join(", ") : "No surfaces selected"}</div>
            </div>
          </div>
        </div>
        <ClaimCheckDetails check={artifact?.claimCheck} />
        {artifact?.intent && (
          <>
            <h4 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Strategic intent</h4>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--foreground)", margin: "0 0 16px" }}>{artifact.intent}</p>
          </>
        )}
        <h4 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px" }}>Full content</h4>
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "18px 20px" }}>
          <FullContentRenderer content={artifact?.fullContent || {}} />
        </div>
      </div>
    );
    footer = (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {ghostBtn("Regenerate", ["M3 12a9 9 0 1 0 9-9", "M3 4v5h5"], onRegenerate)}
        {ghostBtn(copied ? "Copied" : "Copy", ["M9 9h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z", "M5 15a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2"], () => {
          try { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ }
        })}
        <div style={{ flex: 1 }} />
        <button onClick={onApprove} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 600, padding: "9px 17px", borderRadius: 9, border: "none", background: "var(--alpha-teal)", color: "var(--accent-foreground)", cursor: "pointer" }}>
          <Ic paths={["M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z", "m8 12 2.5 2.5L16 9"]} size={15} sw={1.9} /> Approve for Deployment
        </button>
      </div>
    );
  }

  return (
    <div className="ai-redesign">
      <div onClick={onClose} className="ai-fade-in" style={{ position: "fixed", inset: 0, background: "rgba(13,13,15,.32)", zIndex: 60 }} />
      <div className="ai-tray-in" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width, maxWidth: "94vw", background: "var(--card)", borderLeft: "1px solid var(--border)", boxShadow: "var(--elevation-lg)", zIndex: 61, display: "flex", flexDirection: "column" }}>
        <div style={{ position: "relative", borderBottom: "1px solid var(--border)", padding: "22px 56px 20px 28px" }}>
          {header}
          <button onClick={onClose} style={{ position: "absolute", top: 20, right: 22, background: "none", border: "none", cursor: "pointer", color: "var(--muted-foreground)", padding: 2 }}>
            <Ic paths={["M18 6 6 18M6 6l12 12"]} size={20} sw={2} />
          </button>
        </div>
        <div className="ai-scroll" style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>{body}</div>
        {footer && <div style={{ borderTop: "1px solid var(--border)", padding: "16px 28px", background: "var(--card)" }}>{footer}</div>}
      </div>
      <ExplainPopover anchor={explain} onClose={() => setExplain(null)} />
    </div>
  );
}
